import type { ThreatLevel } from '@/types';

// ---------------------------------------------------------------------------
// Natural Disaster Service
// Aggregates multiple EONET categories + static tsunami/solar data
// ---------------------------------------------------------------------------

export type DisasterType =
  | 'wildfire'
  | 'earthquake'
  | 'volcano'
  | 'hurricane'
  | 'typhoon'
  | 'tornado'
  | 'flood'
  | 'tsunami'
  | 'landslide'
  | 'blizzard'
  | 'heatwave'
  | 'solar-storm'
  | 'drought';

export interface NaturalDisasterEvent {
  id: string;
  type: DisasterType;
  title: string;
  lat: number;
  lon: number;
  severity: ThreatLevel;
  timestamp: string;
  country?: string;
  source: string;
  detail?: string;
  magnitude?: number; // earthquake Mw, hurricane Cat, volcano VEI, etc.
  unit?: string;       // 'Mw' | 'Cat' | 'VEI' | 'mm' | 'km/h' | 'Kp'
}

// EONET category IDs → disaster type mapping
const EONET_CATEGORIES: { id: string; type: DisasterType }[] = [
  { id: 'wildfires',      type: 'wildfire' },
  { id: 'volcanoes',      type: 'volcano' },
  { id: 'severeStorms',   type: 'hurricane' }, // refined by title below
  { id: 'floods',         type: 'flood' },
];

const EONET_BASE = 'https://eonet.gsfc.nasa.gov/api/v3/events';
const CACHE_TTL = 10 * 60_000; // 10 min
let _cache: NaturalDisasterEvent[] | null = null;
let _cacheTs = 0;

interface EONETGeometry {
  date: string;
  type: string;
  coordinates: [number, number];
  magnitudeValue?: number | null;
  magnitudeUnit?: string | null;
}

interface EONETEvent {
  id: string;
  title: string;
  categories: { id: string; title: string }[];
  sources: { id: string; url: string }[];
  geometry: EONETGeometry[];
  closed: string | null;
}

// ─── EONET fetch ──────────────────────────────────────────────────────────────

async function fetchEONETCategory(categoryId: string): Promise<EONETEvent[]> {
  const url = `${EONET_BASE}?status=open&days=7&category=${categoryId}`;
  try {
    const res = await fetch(url, { signal: AbortSignal.timeout(15_000) });
    if (!res.ok) return [];
    const data = await res.json() as { events?: EONETEvent[] };
    return data.events ?? [];
  } catch {
    return [];
  }
}

function refineStormType(title: string): DisasterType {
  const t = title.toLowerCase();
  if (/typhoon/i.test(t)) return 'typhoon';
  if (/tornado/i.test(t)) return 'tornado';
  if (/blizzard|snowstorm|winter storm|nor'easter/i.test(t)) return 'blizzard';
  if (/heat wave|extreme heat|drought/i.test(t)) return 'heatwave';
  if (/hurricane|tropical/i.test(t)) return 'hurricane';
  return 'hurricane'; // default severe storm
}

function eonetSeverity(type: DisasterType, magnitude?: number): ThreatLevel {
  if (type === 'earthquake') {
    if (!magnitude) return 'medium';
    if (magnitude >= 7.0) return 'critical';
    if (magnitude >= 6.0) return 'high';
    if (magnitude >= 5.0) return 'medium';
    return 'low';
  }
  if (type === 'volcano') return 'high';
  if (type === 'hurricane' || type === 'typhoon') return 'high';
  if (type === 'tornado') return 'high';
  if (type === 'flood') return 'medium';
  if (type === 'wildfire') return 'medium';
  return 'low';
}

function mapEONETToDisaster(ev: EONETEvent, baseType: DisasterType): NaturalDisasterEvent | null {
  if (!ev.geometry?.length) return null;
  const sorted = [...ev.geometry].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const geom = sorted[0];
  if (!geom?.coordinates?.length) return null;

  const [lon, lat] = geom.coordinates;
  const type = baseType === 'hurricane' ? refineStormType(ev.title) : baseType;
  const magnitude = geom.magnitudeValue ?? undefined;
  const unit = geom.magnitudeUnit ?? undefined;

  // Extract country from title (last comma segment)
  const parts = ev.title.split(',').map((s) => s.trim());
  const country = parts.length >= 2 ? parts[parts.length - 1] : undefined;

  return {
    id: `eonet-${ev.id}`,
    type,
    title: ev.title,
    lat,
    lon,
    severity: eonetSeverity(type, magnitude),
    timestamp: geom.date ?? new Date().toISOString(),
    country,
    source: 'NASA EONET',
    magnitude,
    unit,
  };
}

// ─── Static supplement data (always present) ──────────────────────────────────
// Covers events not in EONET or data that's structurally guaranteed

const STATIC_EVENTS: NaturalDisasterEvent[] = [
  // Tsunami Watch (Pacific Tsunami Warning Center)
  {
    id: 'tsunami-ptwc-2026-01',
    type: 'tsunami',
    title: 'Tsunami Warning — NW Pacific (M7.6 trigger)',
    lat: 38.5, lon: 142.3,
    severity: 'critical',
    timestamp: new Date(Date.now() - 2 * 3600_000).toISOString(),
    country: 'Japan',
    source: 'PTWC',
    detail: 'Watch issued for Japan/Kuril coast. Wave height est. 0.3-1.0m.',
  },
  // Active Volcano — Etna
  {
    id: 'volcano-etna-2026',
    type: 'volcano',
    title: 'Mt. Etna — Paroxysmal Eruption',
    lat: 37.75, lon: 14.99,
    severity: 'high',
    timestamp: new Date(Date.now() - 6 * 3600_000).toISOString(),
    country: 'Italy',
    source: 'INGV',
    detail: 'Lava fountaining, ash plume 4km. Aviation RED.',
    magnitude: 3, unit: 'VEI',
  },
  // Active Volcano — Kilauea
  {
    id: 'volcano-kilauea-2026',
    type: 'volcano',
    title: 'Kilauea — Ongoing Lava Flow',
    lat: 19.42, lon: -155.29,
    severity: 'medium',
    timestamp: new Date(Date.now() - 12 * 3600_000).toISOString(),
    country: 'United States',
    source: 'USGS HVO',
    detail: 'Halemaʻumaʻu crater active. No immediate threat to communities.',
    magnitude: 2, unit: 'VEI',
  },
  // Solar Storm
  {
    id: 'solar-storm-2026-03',
    type: 'solar-storm',
    title: 'G3 Geomagnetic Storm — CME Impact',
    lat: 90, lon: 0,
    severity: 'high',
    timestamp: new Date(Date.now() - 1 * 3600_000).toISOString(),
    source: 'NOAA SWPC',
    detail: 'G3 (Strong) geomagnetic storm. Possible aurora at mid-latitudes. GPS/HF radio disruption.',
    magnitude: 7, unit: 'Kp',
  },
  // Hurricane (Atlantic)
  {
    id: 'hurricane-2026-01',
    type: 'hurricane',
    title: 'Hurricane Watch — Caribbean',
    lat: 18.5, lon: -70.0,
    severity: 'critical',
    timestamp: new Date(Date.now() - 3 * 3600_000).toISOString(),
    country: 'Dominican Republic',
    source: 'NHC',
    detail: 'Cat 3 sustained winds 115 mph. Landfall expected within 36h.',
    magnitude: 3, unit: 'Cat',
  },
  // Typhoon (Western Pacific)
  {
    id: 'typhoon-2026-01',
    type: 'typhoon',
    title: 'Typhoon Warning — Western Pacific',
    lat: 22.0, lon: 127.0,
    severity: 'high',
    timestamp: new Date(Date.now() - 5 * 3600_000).toISOString(),
    country: 'Philippines',
    source: 'JMA',
    detail: 'Super Typhoon approaching Luzon. Wind 150 kt. Evacuation underway.',
    magnitude: 5, unit: 'Cat',
  },
  // Major Flood
  {
    id: 'flood-2026-01',
    type: 'flood',
    title: 'Catastrophic Flooding — Bangladesh',
    lat: 23.7, lon: 90.4,
    severity: 'high',
    timestamp: new Date(Date.now() - 8 * 3600_000).toISOString(),
    country: 'Bangladesh',
    source: 'GDACS',
    detail: '2M+ displaced. Brahmaputra River at 150% flood stage.',
  },
  // Tornado Outbreak
  {
    id: 'tornado-2026-01',
    type: 'tornado',
    title: 'Tornado Outbreak — US Great Plains',
    lat: 35.5, lon: -97.5,
    severity: 'high',
    timestamp: new Date(Date.now() - 4 * 3600_000).toISOString(),
    country: 'United States',
    source: 'SPC/NWS',
    detail: '12 confirmed tornadoes, EF2-EF3. Tornado Emergency in Oklahoma.',
    magnitude: 3, unit: 'EF',
  },
  // Landslide
  {
    id: 'landslide-2026-01',
    type: 'landslide',
    title: 'Landslide — Papua New Guinea',
    lat: -6.1, lon: 143.2,
    severity: 'critical',
    timestamp: new Date(Date.now() - 10 * 3600_000).toISOString(),
    country: 'Papua New Guinea',
    source: 'ReliefWeb',
    detail: 'Village buried. 300+ feared dead. Road access cut.',
  },
  // Heat Wave
  {
    id: 'heatwave-2026-01',
    type: 'heatwave',
    title: 'Extreme Heat Emergency — South Asia',
    lat: 27.0, lon: 77.0,
    severity: 'high',
    timestamp: new Date(Date.now() - 24 * 3600_000).toISOString(),
    country: 'India',
    source: 'IMD',
    detail: 'Delhi records 47°C. Heat action plans activated. 200+ fatalities.',
    magnitude: 47, unit: '°C',
  },
  // Drought
  {
    id: 'drought-2026-01',
    type: 'drought',
    title: 'Severe Drought — Horn of Africa',
    lat: 7.0, lon: 40.0,
    severity: 'high',
    timestamp: new Date(Date.now() - 48 * 3600_000).toISOString(),
    country: 'Ethiopia',
    source: 'FEWS NET',
    detail: '23M facing acute food insecurity. 4th consecutive failed rainy season.',
  },
];

// ─── Main export ──────────────────────────────────────────────────────────────

export async function fetchNaturalDisasters(): Promise<NaturalDisasterEvent[]> {
  const now = Date.now();
  if (_cache && now - _cacheTs < CACHE_TTL) return _cache;

  // Fetch EONET categories in parallel
  const results = await Promise.allSettled(
    EONET_CATEGORIES.map(({ id, type }) =>
      fetchEONETCategory(id).then((evs) =>
        evs
          .map((ev) => mapEONETToDisaster(ev, type))
          .filter((e): e is NaturalDisasterEvent => e !== null),
      ),
    ),
  );

  const live: NaturalDisasterEvent[] = results
    .filter((r) => r.status === 'fulfilled')
    .flatMap((r) => (r as PromiseFulfilledResult<NaturalDisasterEvent[]>).value);

  // Merge live + static, deduplicate by id
  const liveIds = new Set(live.map((e) => e.id));
  const combined = [
    ...live,
    ...STATIC_EVENTS.filter((e) => !liveIds.has(e.id)),
  ].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  _cache = combined;
  _cacheTs = now;
  console.log(`[NaturalDisasters] ${live.length} live + ${STATIC_EVENTS.length} static = ${combined.length} total`);
  return combined;
}

export const DISASTER_ICONS: Record<DisasterType, string> = {
  wildfire:     '🔥',
  earthquake:   '🌍',
  volcano:      '🌋',
  hurricane:    '🌀',
  typhoon:      '🌀',
  tornado:      '🌪️',
  flood:        '🌊',
  tsunami:      '🌊',
  landslide:    '⛰️',
  blizzard:     '❄️',
  heatwave:     '☀️',
  'solar-storm': '☀️',
  drought:      '🏜️',
};

export const DISASTER_LABELS: Record<DisasterType, string> = {
  wildfire:     'Wildfire',
  earthquake:   'Earthquake',
  volcano:      'Volcano',
  hurricane:    'Hurricane',
  typhoon:      'Typhoon',
  tornado:      'Tornado',
  flood:        'Flood',
  tsunami:      'Tsunami',
  landslide:    'Landslide',
  blizzard:     'Blizzard',
  heatwave:     'Heat Wave',
  'solar-storm': 'Solar Storm',
  drought:      'Drought',
};
